import { React, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../services/auth.service';
import { CompanyLogo } from '../../components/companyLogo';
import { Alert, Spin } from 'antd';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { PasswordSecurityCheck, checkPasswordStrength } from '../../components/passwordSecurityCheck';
import localStorageService from '../../services/localStorage.service';
import '../../styles/auth.scss';

export const SetupPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get('token');
    const email = new URLSearchParams(location.search).get('email'); 
    const { setupPassword, resendInvitation, checkToken } = useContext(AuthContext);
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);

    const [formData, setFormData] = useState({
        password1: '',
        password2: '',
        isPassword1Secure: false,
        isPassword2Secure: false
    });

    // Add countdown timer effect
    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setInterval(() => {
                setResendCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendCooldown]);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenExpired(true);
                setIsLoading(false);
                return;
            }

            try {
                const response = await checkToken(token);
                
                if (!response.result || !response.valid) {
                    setTokenExpired(true);
                    setErrorMessage(response.message || "Invalid or expired token");
                }
            } catch (error) {
                console.error('Token validation error:', error);
                setTokenExpired(true);
                setErrorMessage("Failed to validate token. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        validateToken();
    }, [token, checkToken]);

    const handleSetupPassword = (e) => {
        const newPassword = e.target.value;
        const fieldId = e.target.id;
        const isSecure = checkPasswordStrength(newPassword);
        setFormData({...formData, [e.target.name]: newPassword, [fieldId]: isSecure});
        setErrorMessage(null); // Clear any error messages when user types
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
    
        if(formData.password1 === formData.password2) {
            const userData = {
                "token": token,
                "password1": formData.password1,
            }
    
            const response = await setupPassword(userData);
    
            if (response?.result) {   
                // Clear everything except the success flag
                const currentSuccessFlag = localStorage.getItem('passwordSetupSuccess');
                localStorageService.clearLocalStorage();
                localStorage.setItem('passwordSetupSuccess', 'true');
                navigate("/login");
            } else if (response?.token === 'invalid' || response?.token === 'not found') {
                setTokenExpired(true);
            } else {
                setErrorMessage(response?.error || "Failed to set up password. Please try again.");
            }
        } else {
            setErrorMessage("Passwords do not match. Please try again.");
            setFormData({
                password1: '',
                password2: '',
                isPassword1Secure: false,
                isPassword2Secure: false
            });
        }
    };

    const handleResendInvitation = async () => {
        if (!email) {
            setResendStatus({
                type: 'error',
                message: 'Email address is missing. Please contact support.'
            });
            return;
        }

        if (resendCooldown > 0) {
            setResendStatus({
                type: 'error',
                message: `Please wait ${resendCooldown} seconds before requesting another invitation.`
            });
            return;
        }

        setIsResending(true);
        setResendStatus(null);
        
        try {
            const response = await resendInvitation(email);
            
            if (response.result !== false) {
                setResendStatus({
                    type: 'success',
                    message: 'A new activation link has been sent to your email.'
                });
            } else {
                if (response.remainingTime) {
                    setResendCooldown(response.remainingTime);
                }
                if (response.maxAttemptsReached) {
                    setMaxAttemptsReached(true);
                }
                setResendStatus({
                    type: 'error',
                    message: response.error || 'Failed to resend activation link.'
                });
            }
        } catch (error) {
            setResendStatus({
                type: 'error',
                message: 'An error occurred. Please try again.'
            });
        } finally {
            setIsResending(false);
        }
    };

    if (isLoading) {
        return (
            <div className="auth-container set-password-page">
                <div className="bottom-circle"></div>
                <div className="auth-form-container set-password-page">
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    // Update the button render in the expired token view
    const renderResendButton = () => {
        if (maxAttemptsReached) {
            return (
                <button 
                    className="auth-button"
                    disabled={true}
                >
                    Maximum attempts reached
                </button>
            );
        }

        if (resendCooldown > 0) {
            return (
                <button 
                    className="auth-button"
                    disabled={true}
                >
                    Resend available in {resendCooldown}s
                </button>
            );
        }

        return (
            <button 
                onClick={handleResendInvitation}
                className="auth-button"
                disabled={isResending || !email}
            >
                {isResending ? 'Sending...' : 'Resend Activation Link'}
            </button>
        );
    };

    if (tokenExpired) {
        return (
            <div className="auth-container set-password-page">
                <div className="bottom-circle"></div>
                <div className="auth-form-container">
                    <div className="text-center mb-4">
                        <CompanyLogo />
                    </div>
                    
                    <Alert
                        message="Link Expired"
                        description={
                            <div>
                                <p>Your link is no longer valid. It may have already been used or has expired.</p>
                                {email ? (
                                    <p>Click below to request a new activation link.</p>
                                ) : (
                                    <p>Please contact support to request a new activation link.</p>
                                )}
                            </div>
                        }
                        type="error"
                        showIcon
                        className="auth-alert"
                    />
    
                    {resendStatus && (
                        <Alert
                            message={resendStatus.message}
                            type={resendStatus.type}
                            showIcon
                            className="auth-alert"
                        />
                    )}
    
                    <div className="mt-4">
                        {renderResendButton()}
                        <button 
                            onClick={() => navigate('/login')}
                            className="auth-secondary-button mt-3"
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container set-password-page">
            <div className="bottom-circle"></div>
            <div className="auth-form-container">
                <div className="text-center mb-4">
                    <CompanyLogo />
                </div>
                
                <h1 className="auth-heading text-center">Create password</h1>
                
                {errorMessage && (
                    <Alert
                        message="Error"
                        description={errorMessage}
                        type="error"
                        showIcon
                        className="auth-alert"
                    />
                )}
                
                <form method="POST" className="mt-4" onSubmit={handleSubmit}>
                    <div className="password-field-container">
                        <PasswordSecurityCheck 
                            password={formData.password1} 
                            isPasswordSecure={formData.isPassword1Secure}
                        />
                        <input
                            autoComplete="new-password"
                            className="auth-input"
                            required
                            id="isPassword1Secure"
                            placeholder="Password"
                            name="password1"
                            onChange={handleSetupPassword}
                            value={formData.password1}
                            type={showPassword1 ? "text" : "password"}
                        />
                        <label className='eye-slash-icon' htmlFor="check">
                            {showPassword1 ? (
                                <Eye />
                            ) : (
                                <EyeSlash />
                            )}
                        </label>
                        <input
                            id="check"
                            type="checkbox"
                            value={showPassword1}
                            onChange={() => setShowPassword1((prev) => !prev)}
                            style={{ display: 'none' }}
                        />
                    </div>
                    
                    <div className="password-field-container">
                        <PasswordSecurityCheck 
                            password={formData.password2} 
                            isPasswordSecure={formData.isPassword2Secure}
                        />
                        <input
                            autoComplete="new-password"
                            className="auth-input"
                            required
                            id="isPassword2Secure"
                            placeholder="Confirm password"
                            name="password2"
                            onChange={handleSetupPassword}
                            value={formData.password2}
                            type={showPassword2 ? "text" : "password"}
                        />
                        <label className='eye-slash-icon' htmlFor="check2">
                            {showPassword2 ? (
                                <Eye />
                            ) : (
                                <EyeSlash />
                            )}
                        </label>
                        <input
                            id="check2"
                            type="checkbox"
                            value={showPassword2}
                            onChange={() => setShowPassword2((prev) => !prev)}
                            style={{ display: 'none' }}
                        />
                    </div>
                    
                    <div className="mt-4">
                        <button 
                            type="submit"
                            className="auth-button" 
                            disabled={
                                !formData.isPassword1Secure ||
                                !formData.isPassword2Secure ||
                                formData.password1 !== formData.password2
                            }
                        >
                            Log in
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};